const AutoRedeemProfile = require('@/lib/AutoRedemption/AutoRedeemProfile');
const CompanySettings = require('@/lib/Companies/CompanySettings');
const Currency = require('@/lib/Currency/Currency');
const FloatAdjustmentReason = require('@/models/FloatAdjustmentReason');
const PaymentMethod = require('@/lib/PaymentMethods/PaymentMethod');
const TaxRegistration = require('@/lib/Tax/TaxRegistration');
const { container } = require('@/container');
const { toBool, toFloat, toInt, toNullableString } = require('@/functions/transform');

/**
 * @constructor
 */
const Company = function () {
    /**
     * @type {AutoRedeemProfile[]}
     */
    this.autoRedeemProfiles = [];

    /**
     * @type {PaymentMethod[]}
     */
    this.companyPaymentMethods = [];

    this.contactTitlesEnabled = false;
    /**
     * @type {Country}
     */
    this.country = null;
    /**
     * @type {Currency}
     */
    this.currency = null;
    this.currencyID = null;

    /**
     * @type {boolean}
     */
    this.enableOrderEditing = false;

    this.email = null;
    this.receiptEmail = null; // TODO: Replace with company setting version.
    this.floatAdjustmentReasons = [];
    /**
     * @type {boolean}
     */
    this.gratuityEnabled = false;
    this.ID = null;
    this.imageUrl = null;
    this.live = false;
    /**
     * If no max is specified cashback will be disabled.
     *
     * @type {float|null}
     */
    this.maxCashback = null;
    /**
     * @type {string|null}
     */
    this.membershipCardAllocation = null;
    this.name = null;
    this.registeredAddress = null;
    this.registeredCompanyNumber = null;
    this.registeredName = null;
    this.subdomain = null;

    /**
     * @type {boolean}
     */
    this.taxEnabled = false;

    this.taxRegistrations = [];

    this.tel = null;
    this.receiptTel = null; // TODO: Replace with company setting version.
    this.website = null;

    this.googleCloudApiKey = null;

    /**
     * Is stock control enabled for food and drink products? If it is we to check the /productsavailability endpoint
     * when we add items to the cart.
     *
     * @type {boolean}
     */
    this.apiStockControlFoodAndDrinkProducts = false;

    /**
     * Is stock control enabled for products excluding food and drink ? If it is we to check the /productsavailability
     * endpoint when we add items to the cart.
     *
     * @type {boolean}
     */
    this.apiStockControlProductsExcludingFoodAndDrink = false;

    /**
     * @type {CompanySettings}
     */
    this.settings = {};
};

Company.prototype = {
    afterHydration(data) {
        const countryManager = container.countryManager;

        /**
         * Restrict the payment methods to the ones that are allowed and configured appropriately.
         *
         * @type {PaymentMethod[]}
         */
        this.companyPaymentMethods = this.companyPaymentMethods.filter(function (paymentMethod) {
            return !!paymentMethod.showOnEpos || paymentMethod.supportsPartialRefunds;
        });

        // Sort payment methods by navOrder.
        this.companyPaymentMethods.sort(
            /**
             * @param {PaymentMethod} a
             * @param {PaymentMethod} b
             */
            function (a, b) {
                return a.navOrder > b.navOrder ? 1 : -1;
            }
        );

        if (countryManager) {
            this.country = countryManager.findByData(data);
        }
    },

    getHydrationMap() {
        return {
            apiStockControlFoodAndDrinkProducts: toBool,
            apiStockControlProductsExcludingFoodAndDrink: toBool,
            autoRedeemProfiles: {
                modelCollection: AutoRedeemProfile,
            },
            companyPaymentMethods: {
                field: ['companiespaymentmethods', 'companyPaymentMethods'],
                modelCollection: PaymentMethod,
            },
            contactTitlesEnabled: toBool,
            currency: {
                // Has both currencies and currency for compatibility with PRO-675.
                // Both are needed in case the Company model gets rehydrated.
                field: ['currencies', 'currency'],
                model: Currency,
            },
            currencyID: toInt,
            email: {},
            enableOrderEditing: toBool,
            floatAdjustmentReasons: {
                modelCollection: FloatAdjustmentReason,
            },
            googleCloudApiKey: {},
            gratuityEnabled: toBool,
            ID: {
                field: ['ID', 'companyID'],
                transform: toInt,
            },
            imageUrl: {},
            live: {},
            maxCashback: toFloat,
            membershipCardAllocation: {},
            name: {},
            receiptTel: {},
            receiptEmail: {},
            registeredAddress: toNullableString,
            registeredCompanyNumber: toNullableString,
            registeredName: toNullableString,
            settings: {
                model: CompanySettings,
            },
            subdomain: {},
            taxEnabled: {
                field: ['taxEnabled', 'taxStatus'],
                transform: toBool,
            },
            taxRegistrations: {
                modelCollection: TaxRegistration,
            },
            tel: {},
            website: {},
        };
    },

    /**
     * @param {Country} country
     *
     * @return {?TaxRegistration}
     */
    getTaxRegistrationCodeForCountry: function (country) {
        if (!country) {
            // Returning undefined instead of null to match the behaviour of .find().
            return undefined;
        }
        return this.taxRegistrations.find((reg) => reg.countryID === country.ID);
    },

    isLive: function isLive() {
        return !!this.live;
    },

    depositsEnabled() {
        return this.settings?.enablePropointDeposits;
    },

    waiversEnabled() {
        return this.settings?.enableWaivers;
    },

    shouldCaptureTitles: function shouldCaptureTitles() {
        return this.contactTitlesEnabled;
    },

    /**
     * @return {AutoRedeemProfile|null}
     */
    getAutoRedeemProfile: function getAutoRedeemProfile() {
        if (this.autoRedeemProfiles.length > 0) {
            for (let i = 0; i < this.autoRedeemProfiles.length; i++) {
                if (this.autoRedeemProfiles[i].enabled) {
                    return this.autoRedeemProfiles[i];
                }
            }
        }

        return null;
    },

    getReceiptTel() {
        if (this.settings.receiptTel) {
            // New company setting.
            return this.settings.receiptTel;
        }

        if (this.receiptTel) {
            // Legacy receiptTel field.
            return this.receiptTel;
        }

        // Default tel.
        return this.tel;
    },

    getReceiptEmail() {
        if (this.settings.receiptEmail) {
            // New company setting.
            return this.settings.receiptEmail;
        }

        if (this.receiptEmail) {
            // Legacy receiptEmail field.
            return this.receiptEmail;
        }

        // Default email.
        return this.email;
    },

    getReceiptWebsite() {
        if (this.settings.receiptWebsite) {
            // New company setting.
            return this.settings.receiptWebsite;
        }

        // There is no legacy receiptWebsite field.

        // Default website.
        return this.website;
    },
};

module.exports = Company;
